import { useEffect, useRef, useCallback, useState } from 'react';
import createGlobe, { type Marker, type Arc } from 'cobe';
import { PageHeader } from '@/components/ui/page-header';

const BLUE: [number, number, number] = [0.33, 0.59, 1];

const CITIES: { name: string; location: [number, number]; size: number }[] = [
  { name: 'London', location: [51.5074, -0.1278], size: 0.1 },
  { name: 'New York', location: [40.7128, -74.006], size: 0.08 },
  { name: 'San Francisco', location: [37.7749, -122.4194], size: 0.07 },
  { name: 'Paris', location: [48.8566, 2.3522], size: 0.06 },
  { name: 'Tokyo', location: [35.6762, 139.6503], size: 0.07 },
  { name: 'Singapore', location: [1.3521, 103.8198], size: 0.06 },
  { name: 'Sydney', location: [-33.8688, 151.2093], size: 0.06 },
  { name: 'Moscow', location: [55.7558, 37.6173], size: 0.05 },
  { name: 'Mumbai', location: [19.076, 72.8777], size: 0.06 },
  { name: 'Berlin', location: [52.52, 13.405], size: 0.06 },
  { name: 'Dubai', location: [25.2048, 55.2708], size: 0.06 },
  { name: 'São Paulo', location: [-23.5505, -46.6333], size: 0.05 },
  { name: 'Toronto', location: [43.6532, -79.3832], size: 0.05 },
  { name: 'Hong Kong', location: [22.3193, 114.1694], size: 0.06 },
  { name: 'Istanbul', location: [41.0082, 28.9784], size: 0.05 },
  { name: 'Manchester', location: [53.4808, -2.2426], size: 0.07 },
  { name: 'Edinburgh', location: [55.9533, -3.1883], size: 0.06 },
  { name: 'Leeds', location: [53.8008, -1.5491], size: 0.06 },
  { name: 'Birmingham', location: [52.4862, -1.8904], size: 0.06 },
];

const MARKERS: Marker[] = CITIES.map(c => ({
  location: c.location,
  size: c.size,
  color: BLUE,
}));

const ARCS: Arc[] = [
  { from: [51.5074, -0.1278], to: [40.7128, -74.006], color: BLUE },
  { from: [51.5074, -0.1278], to: [48.8566, 2.3522], color: BLUE },
  { from: [51.5074, -0.1278], to: [52.52, 13.405], color: BLUE },
  { from: [51.5074, -0.1278], to: [25.2048, 55.2708], color: BLUE },
  { from: [51.5074, -0.1278], to: [1.3521, 103.8198], color: BLUE },
  { from: [40.7128, -74.006], to: [37.7749, -122.4194], color: BLUE },
  { from: [37.7749, -122.4194], to: [35.6762, 139.6503], color: BLUE },
  { from: [35.6762, 139.6503], to: [22.3193, 114.1694], color: BLUE },
  { from: [22.3193, 114.1694], to: [1.3521, 103.8198], color: BLUE },
  { from: [1.3521, 103.8198], to: [-33.8688, 151.2093], color: BLUE },
  { from: [19.076, 72.8777], to: [25.2048, 55.2708], color: BLUE },
  { from: [40.7128, -74.006], to: [43.6532, -79.3832], color: BLUE },
  { from: [51.5074, -0.1278], to: [53.4808, -2.2426], color: BLUE },
  { from: [51.5074, -0.1278], to: [55.9533, -3.1883], color: BLUE },
];


// ASCII characters from dark to light
const ASCII_CHARS = ' .,:;+*?%S#@';

// Offscreen 2D canvas for reading WebGL pixels
let offscreen: HTMLCanvasElement | null = null;
let offscreenCtx: CanvasRenderingContext2D | null = null;

function canvasToAscii(canvas: HTMLCanvasElement, cols: number): string {
  const w = canvas.width;
  const h = canvas.height;

  if (!offscreen) {
    offscreen = document.createElement('canvas');
    offscreenCtx = offscreen.getContext('2d', { willReadFrequently: true });
  }
  if (!offscreenCtx) return '';

  offscreen.width = w;
  offscreen.height = h;
  offscreenCtx.drawImage(canvas, 0, 0);

  const imageData = offscreenCtx.getImageData(0, 0, w, h);
  const pixels = imageData.data;

  const cellW = w / cols;
  const cellH = cellW * (10 / 7);
  const rows = Math.floor(h / cellH);

  let ascii = '';
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const x = Math.floor(col * cellW + cellW / 2);
      const y = Math.floor(row * cellH + cellH / 2);
      const i = (y * w + x) * 4;
      const r = pixels[i] || 0;
      const g = pixels[i + 1] || 0;
      const b = pixels[i + 2] || 0;
      const brightness = (r + g + b) / 3 / 255;
      const charIndex = Math.floor(brightness * (ASCII_CHARS.length - 1));
      ascii += ASCII_CHARS[charIndex];
    }
    ascii += '\n';
  }
  return ascii;
}

export default function WorldPage() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const asciiRef = useRef<HTMLPreElement>(null);
  const pointerInteracting = useRef<number | null>(null);
  const phiRef = useRef(0.3);
  const targetPhiRef = useRef(0.3);
  const globeRef = useRef<ReturnType<typeof createGlobe> | null>(null);
  const frameRef = useRef<number>(0);
  const [asciiReady, setAsciiReady] = useState(false);

  const ASCII_COLS = 80;

  const animate = useCallback(() => {
    if (!pointerInteracting.current) {
      phiRef.current += 0.002;
      targetPhiRef.current = phiRef.current;
    } else {
      phiRef.current += (targetPhiRef.current - phiRef.current) * 0.15;
    }

    if (globeRef.current && canvasRef.current) {
      const width = canvasRef.current.offsetWidth;
      globeRef.current.update({
        phi: phiRef.current,
        width: width * 2,
        height: width * 2,
      });

      // Update ASCII overlay
      if (asciiRef.current) {
        asciiRef.current.textContent = canvasToAscii(canvasRef.current, ASCII_COLS);
        if (!asciiReady) setAsciiReady(true);
      }
    }

    frameRef.current = requestAnimationFrame(animate);
  }, [asciiReady]);

  useEffect(() => {
    if (!canvasRef.current) return;

    // Patch getContext to force preserveDrawingBuffer so we can read pixels for ASCII
    const origGetContext = canvasRef.current.getContext.bind(canvasRef.current);
    (canvasRef.current as any).getContext = (type: string, attrs?: any) => {
      if (type === 'webgl' || type === 'webgl2') {
        return origGetContext(type, { ...attrs, preserveDrawingBuffer: true });
      }
      return origGetContext(type, attrs);
    };

    const width = canvasRef.current.offsetWidth;

    globeRef.current = createGlobe(canvasRef.current, {
      devicePixelRatio: 2,
      width: width * 2,
      height: width * 2,
      phi: phiRef.current,
      theta: 0.15,
      dark: 1,
      diffuse: 0.4,
      mapSamples: 36000,
      mapBrightness: 8,
      mapBaseBrightness: 0,
      baseColor: [0.15, 0.15, 0.15],
      markerColor: BLUE,
      glowColor: [1, 1, 1],
      opacity: 1,
      markers: MARKERS,
      arcs: ARCS,
      arcColor: BLUE,
      arcWidth: 0.3,
      arcHeight: 0.25,
    });

    frameRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(frameRef.current);
      globeRef.current?.destroy();
    };
  }, [animate]);

  return (
    <>
      <PageHeader title="World" className="mt-page-top mb-header-gap" />

      <div className="w-full px-gutter">
        <div className="bg-card border border-border rounded-xl overflow-hidden relative">
          {/* Globe */}
          <div className="flex items-center justify-center py-8">
            <div className="relative w-full max-w-[600px] aspect-square">
              <pre
                ref={asciiRef}
                className="absolute inset-0 z-10 overflow-hidden transition-opacity duration-1000 cursor-grab active:cursor-grabbing select-none"
                style={{
                  fontSize: '10px',
                  lineHeight: '10px',
                  fontFamily: 'monospace',
                  color: 'rgba(86, 150, 255, 0.85)',
                  opacity: asciiReady ? 1 : 0,
                  letterSpacing: '1px',
                }}
                onPointerDown={(e) => {
                  pointerInteracting.current = e.clientX;
                }}
                onPointerUp={() => {
                  pointerInteracting.current = null;
                }}
                onPointerOut={() => {
                  pointerInteracting.current = null;
                }}
                onMouseMove={(e) => {
                  if (pointerInteracting.current !== null) {
                    const delta = e.clientX - pointerInteracting.current;
                    pointerInteracting.current = e.clientX;
                    targetPhiRef.current += delta / 300;
                  }
                }}
                onTouchMove={(e) => {
                  if (pointerInteracting.current !== null && e.touches[0]) {
                    const delta = e.touches[0].clientX - pointerInteracting.current;
                    pointerInteracting.current = e.touches[0].clientX;
                    targetPhiRef.current += delta / 300;
                  }
                }}
              />
              <canvas
                ref={canvasRef}
                className="w-full h-full invisible"
                style={{ contain: 'layout paint size' }}
              />
            </div>
          </div>

          {/* Stats bar */}
          <div className="border-t border-border px-8 py-5">
            <div className="flex justify-around text-center">
              <div>
                <p className="text-2xl font-semibold">19</p>
                <p className="text-sm text-muted-foreground mt-0.5">Global Markets</p>
              </div>
              <div>
                <p className="text-2xl font-semibold">14</p>
                <p className="text-sm text-muted-foreground mt-0.5">Active Connections</p>
              </div>
              <div>
                <p className="text-2xl font-semibold">6</p>
                <p className="text-sm text-muted-foreground mt-0.5">Continents</p>
              </div>
              <div>
                <p className="text-2xl font-semibold">24/7</p>
                <p className="text-sm text-muted-foreground mt-0.5">Always Running</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
